import { useTranslation } from 'react-i18next';
import { navigateTo } from 'slate/utils';
import { useSlate } from 'slate-react';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { removeBookmark, getBookmarkById } from 'slate/extensions/bookmark';

import { useBookmarks } from 'entities/bookmark';
import Tippy from '@tippyjs/react';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { getLinkById, removeLink } from 'slate/extensions/link';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { useIsViewingVersion } from 'app/router/params';

const BookmarkList = () => {
  const { t } = useTranslation(['bookmarks', 'common']);

  const editor = useSlate();
  const { shadowHost } = useEditorContext();
  const { documentSuperId, superId: sectionId } = useEditorLiveSection();
  const { bookmarkAndLinks, isBookmarksAndLinksLoading, deleteBookmark, deleteLink } = useBookmarks(documentSuperId);
  const isViewingVersion = useIsViewingVersion();
  const handleRemoval = async (id: string, type: string) => {
    switch (type) {
      case 'bookmark':
        deleteBookmark({ id, documentSuperId });
        removeBookmark(id, editor);
        break;
      case 'link':
        deleteLink({ id, documentSuperId });
        removeLink(id, editor);
        break;
      default:
        console.error('Wrong type when deleting, please use bookmark or link');
    }
  };
  const handleNavigation = (elementId: string, type: string) => {
    const found = (type === 'bookmark' ? getBookmarkById : getLinkById)(editor, elementId).found;

    if (found) {
      const [, textPath] = Editor.node(editor, found.path);
      const range = Editor.range(editor, textPath);
      Transforms.select(editor, range);
      ReactEditor.focus(editor);
    }
    navigateTo(shadowHost, elementId);
  };
  if (isBookmarksAndLinksLoading) return <div>{t('common:loading-message')}</div>;
  return (
    <>
      <div className="bookmark-sidebar">
        {bookmarkAndLinks?.map((bookmarkAndLink, index) => {
          const isBookmarkInSameSection = bookmarkAndLink.sectionId === sectionId;
          const canRemoveBookmark = isBookmarkInSameSection && !isViewingVersion && bookmarkAndLink.links.length === 0;
          return (
            <div key={index} className="bookmark-sidebar-content">
              <div className="bookmark-sidebar-bookmark-content">
                <Tippy
                  content={
                    <div className="bg-gray-100 p-2 rounded">
                      {!isBookmarkInSameSection
                        ? bookmarkAndLink.sectionName
                        : bookmarkAndLink.links.length !== 0
                        ? t('bookmarks:sidebar.remove-connected-links')
                        : ''}
                    </div>
                  }
                  disabled={isBookmarkInSameSection && bookmarkAndLink.links.length === 0}
                >
                  <i
                    className={`fa-solid fa-trash ${!canRemoveBookmark && 'link-bookmark-disabled'}`}
                    onClick={() => canRemoveBookmark && handleRemoval(bookmarkAndLink.bookmarkId, 'bookmark')}
                  />
                </Tippy>
                <i
                  className={`fa-regular fa-bookmark ${!isBookmarkInSameSection && 'link-bookmark-disabled'}`}
                  onClick={() => {
                    isBookmarkInSameSection && handleNavigation(bookmarkAndLink.bookmarkId, 'bookmark');
                  }}
                />
                <p>{bookmarkAndLink.label}</p>
              </div>
              <i className="fa-regular fa-arrow-right bookmark-sidebar-content-icon" />
              <div className="bookmark-sidebar-link-content-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}>
                {bookmarkAndLink.links.length === 0 ? (
                  <p className="bookmark-sidebar-link-content-inner">{t('bookmarks:sidebar.no-links')} </p>
                ) : (
                  bookmarkAndLink.links.map((link) => {
                    const isLinkInSameSection = link.sectionId === sectionId;
                    const canRemoveLink = isLinkInSameSection && !isViewingVersion;
                    return (
                      <div key={link.id} className="bookmark-sidebar-link-content-inner">
                        <p>{link.label}</p>
                        <i
                          className={`fa-regular fa-link ${!isLinkInSameSection && 'link-bookmark-disabled'}`}
                          onClick={() => {
                            isLinkInSameSection && handleNavigation(link.linkId, 'link');
                          }}
                        />
                        <Tippy content={<div className="bg-gray-100 p-2 rounded">{link.sectionName}</div>} disabled={isLinkInSameSection}>
                          <i
                            className={`fa-solid fa-trash ${!canRemoveLink && 'link-bookmark-disabled'}`}
                            onClick={() => canRemoveLink && handleRemoval(link.linkId, 'link')}
                          />
                        </Tippy>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="divider" />
    </>
  );
};

export const Bookmarks = () => {
  const { t } = useTranslation('bookmarks');

  return (
    <div className="preview--container">
      <div className="px-3 pt-4 pb-3 w-100 bg-white sticky-top">
        <div className="fw-bold">{t('sidebar.title')}</div>
      </div>
      <div className="px-3 w-100">
        <BookmarkList />
      </div>
    </div>
  );
};
